// External Imports
import { useState, useContext, useEffect } from "react";
import { MdAdd, MdRefresh } from "react-icons/md";
import { useLocation } from "react-router-dom";

// Internal Imports
import { UtilityContext } from "../../context/util-context";
import { requestAdminAPI } from "../../api/posh-api";
import SearchBar from "../UI/SearchBar";
import Heading from "../UI/Heading";
import DateFilter from "../UI/DateFilter";
import PaginationMenu from "../UI/PaginationMenu";
import LoadingCard from "../UI/LoadingCard";
import NoRecordsFound from "../UI/NoRecordsFound";
import ExportCsvButton from "../UI/ExportCsvButton";
import { toTitleCase, truncate } from "../../util/format-text";
import ListLabels from "../UI/ListLabels";
import { isoDateToString } from "../../util/format-date";

/** ========================================================================
 * * Announcements
 * Renders a page, which displays all bookings.
 * ========================================================================== */

export default function Announcements() {
  const { navigate } = useContext(UtilityContext);
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  // Default Filters
  const defaultFilters = {
    limit: 50,
    query: "",
    sd: "",
    ed: "",
  };

  // Filters in URL params
  const paramFilters = {};
  Object.keys(defaultFilters).forEach(
    (key) => (paramFilters[key] = params.get(key) ?? defaultFilters[key])
  );

  // Initialize States
  const [announcements, setAnnouncements] = useState([]);
  const [filter, setFilter] = useState(paramFilters);
  const [loading, setLoading] = useState(true);

  /* ======================= FETCH RECORDS ======================= */
  const fetchRecords = async () => {
    try {
      setLoading(true);

      // Send Request
      let filters = [];
      if (filter.sd && filter.ed) {
        filters.push(`dateCreated: { ge: "${filter.sd}"}`);
        filters.push(`dateCreated: { le: "${filter.ed}"}`);
      }
      if (filter.query) filters.push(`message: { contains: "${filter.query}"}`);
      const data = await requestAdminAPI("filter", {
        table: "announcement",
        limit: filter.limit,
        filters: filters,
        columns: ["createdBy { name }"],
      });

      // Replace URL
      let url = "/announcements?";
      Object.keys(filter).forEach((key) => {
        url += `${key}=${filter[key] ?? defaultFilters[key]}&`;
      });
      navigate(url, {}, { replace: true });

      setAnnouncements(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // Fetch data on load and when filter changes
  useEffect(() => {
    fetchRecords();
  }, [filter]);

  return (
    <div className="container">
      <Heading title="Announcements">
        <DateFilter
          startDate={filter.sd}
          endDate={filter.ed}
          onSubmit={(sd, ed) => setFilter({ ...filter, sd, ed })}
        />
        <MdAdd
          onClick={(e) => navigate("/announcements/add", e)}
          className="text-3xl icon"
          title="Add Announcement"
        />
        <MdRefresh onClick={fetchRecords} className="icon" title="Refresh" />
        <ExportCsvButton array={announcements} filename="announcements" />
      </Heading>

      <SearchBar
        searchHandler={(query) => setFilter({ ...filter, query })}
        clearSearch={() => setFilter(defaultFilters)}
        searchValue={filter.query}
      />

      {loading ? (
        <LoadingCard columns={5} max={6} />
      ) : announcements.length > 0 ? (
        <div className="gap-3 list">
          <div className="labels lg:grid-cols-7">
            <ListLabels
              labels={[
                "Date Created",
                { name: "Message", classes: "lg:col-span-2" },
                "Description",
                "Recipient Type",
                "Recipients",
                "Created By",
              ]}
              selected={filter.sort}
              setSelected={(sort) => setFilter({ ...filter, sort })}
            />
          </div>
          {announcements.map((announcement, idx) => (
            <div className="grid pill lg:grid-cols-7" key={idx} onClick={(e) => navigate(`/announcements/info?id=${announcement.id}`, e)}>
              <p>{isoDateToString(announcement.dateCreated)}</p>
              <p className="lg:col-span-2">
                {truncate(announcement.message, 30)}
              </p>
              <p>{truncate(announcement.description, 15)}</p>
              <p>{toTitleCase(announcement.recipientType)}s</p>
              <p>{announcement.recipientNames?.length || 0}</p>
              <p>{announcement.createdBy?.name || "Unknown"}</p>
            </div>
          ))}
        </div>
      ) : (
        <NoRecordsFound />
      )}

      <PaginationMenu
        limit={filter.limit}
        setLimit={(l) => setFilter({ ...filter, limit: l })}
        loading={loading}
        count={announcements.length}
      />
    </div>
  );
}
